
import { Auth, CognitoUser } from '@aws-amplify/auth'
import { Amplify, Hub } from '@aws-amplify/core'
import { HubCallback } from '@aws-amplify/core/lib/Hub'
import { CognitoUserSession } from 'amazon-cognito-identity-js'
import { createContext, useCallback, useContext, useEffect } from 'react'
import { HDAACCOUNTSARRAY } from 'constants/accounts'
import { projectClient } from 'constants/project'
import useAuthStore from 'store/auth'

export type CognitoAttributes =
  | Record<string, string>
  | Array<{ Name: string; Value: string }>

export type CognitoUserInfo = {
  id: string
  username: string
  attributes: Record<string, string>
}

const INCORRECT_CREDENTIALS_ERROR = 'Incorrect username or password.'

const hasMatchingModuleCode = (userInfo: CognitoUserInfo | null): boolean => {
  if (!userInfo) return false

  const attributes = userInfo.attributes as CognitoAttributes
  const moduleCode = Array.isArray(attributes)
    ? attributes.find((attribute) => attribute.Name === 'custom:moduleCode')
      ?.Value
    : attributes['custom:moduleCode']

  if (!moduleCode) return false

  if (projectClient === 'generic') {
    return HDAACCOUNTSARRAY.some(
      (account) =>
        account.logo.startsWith('/accounts/generic/') &&
        account.project === moduleCode.toLowerCase()
    )
  }

  return (
    Boolean(process.env.REACT_APP_MODULE_CODE) &&
    moduleCode === process.env.REACT_APP_MODULE_CODE
  )
}

const COGNITO_CONFIG = {
  REGION: process.env.REACT_APP_COGNITO_REGION,
  USER_POOL_ID: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  APP_CLIENT_ID: process.env.REACT_APP_COGNITO_APP_CLIENT_ID,
}

const defaultSignIn = (username: string, password: string): Promise<CognitoUser> =>
  Auth.signIn(username, password)

interface IAuthContext {
  signIn(username: string, password: string): Promise<CognitoUser>
  signOut(): Promise<void>
  forgotPassword(username: string): Promise<CognitoUser>
  forgotPasswordSubmit(
    username: string,
    code: string,
    newPassword: string
  ): Promise<string>
  getSession: () => Promise<CognitoUserSession | null>
  getCurrentInfo: () => Promise<CognitoUserInfo | null>
}

Amplify.configure({
  Auth: {
    mandatorySignId: true,
    region: COGNITO_CONFIG.REGION,
    userPoolId: COGNITO_CONFIG.USER_POOL_ID,
    userPoolWebClientId: COGNITO_CONFIG.APP_CLIENT_ID,
  },
})

const signOut = (): Promise<void> => Auth.signOut()

const forgotPassword = (username: string): Promise<CognitoUser> =>
  Auth.forgotPassword(username)

const forgotPasswordSubmit = (
  username: string,
  code: string,
  newPassword: string
): Promise<string> => Auth.forgotPasswordSubmit(username, code, newPassword)

const getSession = (): Promise<CognitoUserSession | null> =>
  Auth.currentSession()

export const getCurrentInfo = (): Promise<CognitoUserInfo | null> =>
  Auth.currentUserInfo() as Promise<CognitoUserInfo | null>


export const useCognito = (): IAuthContext => {
  const authSignIn = useAuthStore((state) => state.signIn)
  const authSignOut = useAuthStore((state) => state.signOut)

  const signIn = useCallback(
    async (username: string, password: string): Promise<CognitoUser> => {
      const cognitoUser = await Auth.signIn(username, password)
      const userInfo = await getCurrentInfo()

      if (!hasMatchingModuleCode(userInfo)) {
        authSignOut()
        try {
          await Auth.signOut()
        } catch {
          // Ignore no-empty rule
        }
        throw new Error(INCORRECT_CREDENTIALS_ERROR)
      }

      const token = cognitoUser
        .getSignInUserSession()
        ?.getAccessToken()
        .getJwtToken()

      if (!token) throw new Error(INCORRECT_CREDENTIALS_ERROR)

      await authSignIn(username, token)
      return cognitoUser
    },
    [authSignIn, authSignOut]
  )
  // const [user, setUser] = useState<IUser | null>()
  // const sessionLogin = useStoreActions((actions) => actions.session.login)
  // const sessionLogout = useStoreActions((actions) => actions.session.logout)
  // const setLoading = useStoreActions((actions) => actions.session.setLoading)

  const authListener: HubCallback = useCallback(
    ({ payload: { event } }) => {
      switch (event) {
        case 'signOut':
          authSignOut()
          // setUser(null)
          // sessionLogout()
          break
      }
    },
    [authSignIn, authSignOut]
  )

  useEffect(() => {
    getSession()
      .then((session) => {
        if (session && session.isValid()) {
          getCurrentInfo()
            .then((userInfo) => {
              if (!hasMatchingModuleCode(userInfo)) {
                authSignOut()
                return Auth.signOut()
              }

              const { username } = userInfo as CognitoUserInfo
              authSignIn(
                username,
                session.getAccessToken().getJwtToken()
              ).catch((error) => {
                authSignOut()

                // setUser(null)
                // sessionLogout()
              })

              // const cognitoToken = session.getAccessToken()
              // setUser({ username, cognitoToken })
              // sessionLogin({ cognitoToken: cognitoToken.getJwtToken() })
            })
            .catch(() => {
              authSignOut()

              // setUser(null)
              // sessionLogout()
            })
        }
      })
      .catch(() => {
        // console.log({ error })
        // toast.error(error)
        authSignOut()
        // setLoading()
        // setUser(null)
        // sessionLogout()
      })
    // }, [sessionLogin, sessionLogout, setLoading])
  }, [authSignIn, authSignOut])

  useEffect(() => {
    Hub.listen('auth', authListener)
    return () => Hub.remove('auth', authListener)
  }, [authListener])

  return { signIn, signOut, forgotPassword, forgotPasswordSubmit, getSession, getCurrentInfo }
}

export const AuthContext = createContext<IAuthContext>({
  signIn: defaultSignIn,
  signOut,
  forgotPassword,
  forgotPasswordSubmit,
  getSession,
  getCurrentInfo,
})

export const useAuth = (): IAuthContext => useContext(AuthContext)
